import {TimeSpan} from '@sparkware/app-utils';
import {ServiceBase} from '../ServiceBase';
import {IRegulation, IRegulationService} from './regulation.service.interface';
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {LegacyRegulationWrapper} from "./legacy-regulation-wrapper";
import {NullableString} from "../../../common/types/nullable-types";
import {CGP_ERROR_CODES} from "../../../sdk/src/exceptions/cgp_error_codes";
import { observable, makeObservable } from "mobx";
import {REGULATION_CODE_TYPES} from '../../../sdk/src/services/regulation/regulation_const';
import {GermanyRegulation} from './germany/germany.regulation'
import {MichiganRegulation} from "./michigan/michigan.regulation";
import {OntarioRegulation} from "./ontario/ontario.regulation";
import {NetherlandsRegulation} from "./netherland/netherlands.regulation";
import {NewJerseyRegulation} from "./new-jersey/new-jersey.regulation";
import {UkRegulation} from "./uk/uk.regulation";
import {ItalyRegulation} from "./italy/italy.regulation";
import {RomaniaRegulation} from "./romania/romania.regulation";
import {PortugalRegulation} from "./portugal/portugal.regulation";
import {SwedenRegulation} from "./sweden/sweden.regulation";
import {DenmarkRegulation} from "./denmark/denmark.regulation";
import {SpainRegulation} from "./spain/spain.regulation";
import {IRegulationProxyService} from "./regulation-proxy.service.interface";


export class RegulationService extends ServiceBase implements IRegulationService, IRegulationProxyService {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this.reaction(() => this.services.user.isAuthorized,
            () => {
                this.current = this._createRegulation();
            }, {
            fireImmediately: true
        });
    }
    getRegulationCode(): number {
        return this.services.sdk.getRegulationService().getRegulationCode();
    }

    isUSRegulation(): boolean {
        return this.services.sdk.getRegulationService().isUSRegulation();
    }

    get forceDemoButton(): boolean {
        return this.services.sdk.getRegulationService().forceDemoButton();
    }

    getSessionCooldownConfig(): {featureEnabled: boolean; cooldownTime: TimeSpan} {

        const sessionCooldownConfig = this.services.sdk.getRegulationService().getSessionCooldownConfig();

        return {
            ...sessionCooldownConfig,
            cooldownTime: TimeSpan.parse(sessionCooldownConfig.cooldownTime)
        };
    }

    @observable.ref
    current: IRegulation;


    shouldShowPlayerLimitsToaster(): boolean {
        return this.services.sdk.getRegulationService().shouldShowPlayerLimitsToaster();
    }

    shouldEnforceHybridAppForMobileWeb(): boolean {
        return this.services.device.isMobileWebApp && this.services.configuration.servicesConfigs.Skin.enforceHybridAppForMobileWeb;
    }

    shouldPreventOpenGameOnMobileWeb(): boolean {
        return this.services.configuration.servicesConfigs.Skin.preventOpenGameOnMobileWeb;
    }


    getErrorMessage(errorCode: number): NullableString {
        return this.services.sdk.getRegulationService().getErrorMessage(errorCode);
    }

    getGeneralErrorMessage(): string {
        return this.getErrorMessage(CGP_ERROR_CODES.GENERAL_ERROR);
    }

    logoutUser(): Promise<void>{
        return this.current.logoutUser();
    }

    isLoginPopupEnabled(): boolean {
        return this.services.sdk.getRegulationService().isLoginPopupEnabled();
    }

    private _createRegulation(): IRegulation {
        const regulationCode = this.getRegulationCode();
        const legacyRegulation = this.services.sdk.currentRegulation;
        switch (regulationCode) {
            case REGULATION_CODE_TYPES.GERMANY:
                return new GermanyRegulation(legacyRegulation, regulationCode, this.services);
            case REGULATION_CODE_TYPES.MICHIGAN:
                return new MichiganRegulation(legacyRegulation, regulationCode, this.services);
            case REGULATION_CODE_TYPES.CANADA_ONTARIO:
                return new OntarioRegulation(legacyRegulation, regulationCode, this.services)
            case REGULATION_CODE_TYPES.NETHERLANDS:
                return new NetherlandsRegulation(legacyRegulation, regulationCode, this.services);
            case REGULATION_CODE_TYPES.NJ:
                return new NewJerseyRegulation(legacyRegulation, regulationCode, this.services);
            case REGULATION_CODE_TYPES.UK:
                return new UkRegulation(legacyRegulation, regulationCode, this.services);
            case REGULATION_CODE_TYPES.ITALY:
                return new ItalyRegulation(legacyRegulation, regulationCode, this.services);
            case REGULATION_CODE_TYPES.ROMANIA:
                return new RomaniaRegulation(legacyRegulation, regulationCode, this.services);
            case REGULATION_CODE_TYPES.PORTUGAL:
                return new PortugalRegulation(legacyRegulation, regulationCode, this.services);
            case REGULATION_CODE_TYPES.SWEDEN:
                return new SwedenRegulation(legacyRegulation, regulationCode, this.services);
            case REGULATION_CODE_TYPES.DK:
                return new DenmarkRegulation(legacyRegulation, regulationCode, this.services);
            case REGULATION_CODE_TYPES.SPAIN:
                return new SpainRegulation(legacyRegulation, regulationCode, this.services);
            default:
                return new LegacyRegulationWrapper(legacyRegulation, regulationCode, this.services);
        }
    }

    showRealityCheckMessage(): Promise<void> {
        return this.current.showRealityCheckMessage();
    }

    get shouldShowExitGamePopup(): boolean {
        return true;
    }
}
